import {naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import addColumn from 'data-frames/util/addColumn';
import coerceOrFilterNulls from 'data-frames/util/coerceOrFilterNulls';
import {DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import filterRows from 'data-frames/util/filterRows';
import updateColumnDef from 'data-frames/util/updateColumnDef';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  AnalyticsVisualizationAxisScale,
  AnalyticsVisualizationColorPalette,
  AnalyticsVisualizationLegendLocation,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import makeVisualizationConfig from 'insights/universal-blocks/makeVisualizationConfig';
import {smallSpacingRight} from 'styles/Utilities';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import ImmutableSet from 'util/ImmutableSet';
import {JSONValue} from 'util/Types';

import {ParameterOptimizationFeature, ShapValuesColumnsMapping} from '../ParameterOptimizationTypes';
import {getFeatureList} from '../ParameterOptimizationUtils';
import ModelingDashboardBlock, {SharedModelingBlockProps} from './ModelingDashboardBlock';
import {SHAP_VALUE_COLUMN_SYSTEM_NAME, SHAP_VALUE_Y_AXIS_LABEL} from './ModelingDashboardConstants';

export const FEATURE_CONTRIBUTION_BLOCK_TITLE = 'Parameter contribution';
const FEATURE_CONTRIBUTION_BLOCK_DESCRIPTION =
  'The parameter value is plotted against how much it contributed to the experimental output.';

/** @package */
export const FEATURE_VALUE_COLUMN_SYSTEM_NAME = 'typed_feature_value';

/** @package */
export const MAX_PREDICTIONS_TO_SHOW = 1000;
/** @package */
export const VISUALIZATION_TYPE = VisualizationType.SCATTER_PLOT;
/** @package */
export const BASE_VISUALIZATION_PARAMETERS = {
  aggregationType: null,
  colorPalette: AnalyticsVisualizationColorPalette.GRADIENT,
  errorBarType: null,
  legendLocation: AnalyticsVisualizationLegendLocation.RIGHT,
  outlierConfig: null,
  regressionConfig: null,
  rowIdColumnName: null,
  seriesColumnName: null,
  xAxisScale: AnalyticsVisualizationAxisScale.LINEAR,
  yAxisIncludesZero: false,
  yAxisMin: null,
  yAxisMax: null,
  yAxisScale: AnalyticsVisualizationAxisScale.LINEAR,
};

const selectedFeatureGlobalState = makeUseGlobalClientState<ParameterOptimizationFeature | null>(null);

export interface FeatureContributionBlockProps extends SharedModelingBlockProps {
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>;
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>;
  featureValuesAccessor: DataFrameValueAccessor;
  dataFrame: DataFrame;
  columnsMapping: ShapValuesColumnsMapping;
}

/**
 * Return the type of some features used for visualizing feature contribution with `makeVisualizationData`. See
 * `makeVisualizationData` for details.
 *
 * @package
 * */
export function getFeatureValuesType(featureValues: Array<JSONValue>): LeafType.DECIMAL | LeafType.TEXT {
  const featureValueJSTypesSet = new ImmutableSet(featureValues.map((featureValue) => typeof featureValue));

  if (
    featureValueJSTypesSet.has('number') &&
    assertAndContinue(
      featureValueJSTypesSet.size() === 1 ||
        (featureValueJSTypesSet.size() === 2 && featureValueJSTypesSet.has(typeof null)),
      'Expected all feature values for feature to be the same type',
      {featureValues, featureValueJSTypesSet}
    )
  ) {
    return LeafType.DECIMAL;
  }

  return LeafType.TEXT;
}

/**
 * Given a shap values data frame and a selected `ParameterOptimizationFeature`, return the data frame to render
 * the feature contribution chart for that feature.
 *
 * To generate the visualization data frame, perform the following transformations:
 * 1. Filter shap values to only base features and base entities matching `ParameterOptimizationFeature`
 * 2. Add a new column representing the typed version of `featureValue`s
 *    - `featureValues` is of type `JSON`, but for a given filtered feature, it is expected to be a more specific
 *    type. For the purpose of Parameter Optimization there are only 2 types of variables that are useful to render
 *    here - categorical and numerical variables^. To simplify inferring the types from the subset of `JSON` values
 *    we only either use `TEXT` or `DECIMAL`. This also means that we can always run `String(value)` to get the
 *    correctly serialized value for some `JSON`.
 * 3. Rename the `shapValue` column's display name to `SHAP_VALUE_Y_AXIS_LABEL` in order to the correct y-axis
 * label.
 *
 * ^Dates and timestamps should be considered as categorical variables here because their inclusions are either
 * an error (e.g. batch date, which will likely always be the best predictor for output) or are real categories
 * (e.g. acquisition date of some product).
 *
 * @package
 */
export function makeVisualizationData(
  dataFrame: DataFrame,
  columnsMapping: ShapValuesColumnsMapping,
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>,
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>,
  featureValuesAccessor: DataFrameValueAccessor,
  selectedFeature: ParameterOptimizationFeature
): DataFrame {
  const selectedFeatureShapValues = filterRows(
    dataFrame,
    (row) =>
      baseEntitiesAccessor(row)[0] === selectedFeature.baseEntity &&
      baseFeaturesAccessor(row)[0] === selectedFeature.baseFeature
  );

  const featureValues = selectedFeatureShapValues.rows.map((row) => featureValuesAccessor(row));
  const featureValuesLeafType = getFeatureValuesType(featureValues);

  let visualizationDataFrame = addColumn(
    selectedFeatureShapValues,
    {
      systemName: FEATURE_VALUE_COLUMN_SYSTEM_NAME,
      displayName: `${selectedFeature.displayName} value`,
      type: {leafType: featureValuesLeafType},
    },
    (_, index) => (featureValues[index] == null ? null : String(featureValues[index]))
  );

  visualizationDataFrame = updateColumnDef(visualizationDataFrame, columnsMapping.shapValue, {
    displayName: SHAP_VALUE_Y_AXIS_LABEL,
  });

  return coerceOrFilterNulls(visualizationDataFrame, FEATURE_VALUE_COLUMN_SYSTEM_NAME);
}

export default function FeatureContributionBlock({
  baseEntitiesAccessor,
  baseFeaturesAccessor,
  dataFrame,
  columnsMapping,
  featureValuesAccessor,
  ...sharedModelingBlockProps
}: FeatureContributionBlockProps): JSX.Element {
  const {dashboardClientStateId} = sharedModelingBlockProps;
  const features = naturalSortByKey(
    getFeatureList(dataFrame, baseEntitiesAccessor, baseFeaturesAccessor),
    'displayName'
  );

  const [maybeSelectedFeature, setSelectedFeature] =
    selectedFeatureGlobalState.useGlobalClientState(dashboardClientStateId);
  const selectedFeature = maybeSelectedFeature ?? features[0];

  const visualizationDataFrame = makeVisualizationData(
    dataFrame,
    columnsMapping,
    baseEntitiesAccessor,
    baseFeaturesAccessor,
    featureValuesAccessor,
    selectedFeature
  );

  const visualizationConfig = makeVisualizationConfig(VISUALIZATION_TYPE, {
    ...BASE_VISUALIZATION_PARAMETERS,
    xAxisColumnName: FEATURE_VALUE_COLUMN_SYSTEM_NAME,
    yAxisColumnName: SHAP_VALUE_COLUMN_SYSTEM_NAME,
    seriesColumnName: columnsMapping.predictedOutput,
  });

  const featureDropdown = (
    <SelectDropdown
      className={css(styles.featureDropdown)}
      displayName={
        <div className={css(styles.featureDropdownDisplayNameWrapper)}>{selectedFeature.displayName}</div>
      }
      menu={
        <SearchableMenu
          height={MenuHeight.MEDIUM}
          width={MenuWidth.FULL}
          optionDatas={features.map((feature) => ({
            name: feature.displayName,
            value: feature,
          }))}
          onSelect={(feature) => {
            setSelectedFeature(feature);
          }}
          selectedValue={selectedFeature}
        />
      }
    />
  );

  return (
    <ModelingDashboardBlock
      {...sharedModelingBlockProps}
      blockTitle={FEATURE_CONTRIBUTION_BLOCK_TITLE}
      blockDescription={FEATURE_CONTRIBUTION_BLOCK_DESCRIPTION}
      inputDataFrame={visualizationDataFrame}
      toolbarExtension={featureDropdown}
      visualizationConfig={visualizationConfig}
    />
  );
}

const styles = StyleSheet.create({
  featureDropdown: {
    ...smallSpacingRight,
  },
  featureDropdownDisplayNameWrapper: {
    // `SelectDropdown` gives `overflow-wrap: break-word`. This makes the toolbar taller for long feature names,
    // which is undesirable. Instead we want to handle overflow with ellipsis, so we first need to undo setting
    // `break-word`.
    overflowWrap: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: 250,
  },
});
